import { useEffect, useRef, type ReactNode } from 'react';
import { useHydrated } from 'remix-utils';
import { cn } from '@rocco/utils/cn';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import { BackToTop } from '~/ui-rocco/components/BackToTop';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { BulkEnquire } from '~/modules/enquiry/bulk/BulkEnquire';
import { BulkEnquiryEnabledProvider } from '~/modules/enquiry/bulk/BulkEnquireTileControl';
import { useSectionInView } from '~/modules/enquiry/bulk/use-section-in-view';
import { useContentGateReached } from '~/modules-rocco/content-gate/content-gate-reached';
import { useContentGateActive } from '~/modules-rocco/content-gate/use-content-gate-active';
import { useScrollToPillBar } from './use-scroll-to-pill-bar';
import {
    CONTENT_CAP,
    MagicIsland,
    MagicIslandProvider,
} from '~/ui-rocco/pages/detail-shared';
import {
    DirectoryBrowseThemeProvider,
    type DirectoryBrowseTheme,
} from './directory-browse-theme';
import {
    DirectoryFilterDrawerProvider,
    useDirectoryFilterOpenState,
} from './directory-filter-drawer';

export type DirectoryEntityType = 'products' | 'professionals' | 'projects';

/**
 * Watermark title per vertical. Fixed per directory rather than derived from
 * the active category, so it reads the same at every depth. A directory that
 * lists more than one thing overrides it per mode — see `verticalTitle`.
 */
const VERTICAL_TITLE: Record<DirectoryEntityType, string> = {
    products: 'Products',
    professionals: 'Professionals',
    projects: 'Projects',
};

interface DirectoryBrowsePageProps {
    entityType: DirectoryEntityType;
    /**
     * Overrides the background watermark. Products lists two things under one
     * entity type — products and the brands behind them — so the word has to
     * follow the active browse mode rather than the index being read.
     */
    verticalTitle?: string;
    /**
     * Page theme. Projects renders dark (editorial near-black); products and
     * professionals render light. Default light.
     */
    theme?: DirectoryBrowseTheme;
    /** Eyebrow + editorial heading + Get Matched panel (intro slot). */
    intro?: ReactNode;
    /**
     * Browse-mode nav sitting under the intro (Projects / Spaces / Styles).
     * When supplied the intro's trailing gap tightens so the tabs read as part
     * of the page head rather than a separate band.
     */
    tabs?: ReactNode;
    /** Top "explore categories" image-card carousel (rendered in the category-carousel slot). */
    categoryCarousel?: ReactNode;
    /** Shared filter/category-pill bar for this entity (rendered in the pill-bar slot). */
    pillBar?: ReactNode;
    /**
     * Persistent left filter sidebar (facet panel). When provided, the pill bar's
     * "Show Filters" toggle reveals it beside the grid. Omit for pages without
     * facet filters.
     */
    sidebar?: ReactNode;
    /** Main content — the product/entity grid (rendered in the rows slot). */
    rows?: ReactNode;
    /**
     * True while a full-screen overlay owns the page (the Spaces / Styles image
     * preview). The floating enquiry prompt is suppressed for its duration so
     * the reader is left with the overlay alone.
     */
    overlayOpen?: boolean;
    /**
     * Full-width SSR content rendered below the grid (recently viewed, explore
     * all categories, newspaper FAQ). Sits outside the collapsible sidebar
     * layout so it spans the full page width and stays crawlable.
     */
    belowGrid?: ReactNode;
}

/**
 * Carousel-driven browse experience for the directory pages (5.0 revamp),
 * shared across /products, /professionals and /projects and rendered behind the
 * revamp_2026 flag. Composes the shared sections passed in by
 * each entity's browse container with the Figma page spacers between them, and
 * mounts the Magic Island (used by the category carousel for pagination /
 * auto-slide, and later the Bulk Enquire flow).
 */
/**
 * The vertical's name as a watermark behind the page.
 *
 * It travels at half the page's rate, so it drifts against the content rather
 * than riding with it, and sits behind everything with a heavy blur — it is
 * depth, not a heading. `aria-hidden` because the nav and the breadcrumb
 * already announce the vertical, and `pointer-events-none` so it never
 * intercepts a click.
 *
 * The transform is written straight to the node inside a rAF rather than held
 * in state: this runs on every scroll frame, and a re-render per frame would
 * cost far more than the paint.
 */
const PARALLAX_RATE = 0.5;

/**
 * How far the mark may drift before it holds. It paints above the header, so
 * left unbounded it would eventually travel down over the filter bar and the
 * grid — the drift is the effect, covering the page is not.
 */
const PARALLAX_MAX = 150;

export const VerticalWordmark = ({
    isDark,
    title,
    className,
    markClassName,
}: {
    isDark: boolean;
    title: string;
    className?: string;
    /** The mark's own face and colour, where a page sets its own. */
    markClassName?: string;
}) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        let frame = 0;
        const apply = () => {
            frame = 0;
            const drift = Math.min(
                window.scrollY * PARALLAX_RATE,
                PARALLAX_MAX,
            );
            el.style.transform = `translate3d(0, ${drift}px, 0)`;
        };
        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(apply);
        };

        apply();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    return (
        <div
            ref={ref}
            aria-hidden="true"
            className={cn(
                CONTENT_CAP,
                // Sits beside the logomark, which is the last thing in the
                // header — so the mark lands inside that opaque white box and
                // has to clear it. It occupies the header's empty right half
                // and covers nothing: the nav is left and centre, the page
                // content starts below. PARALLAX_MAX stops it drifting down
                // into that content as the page scrolls.
                'pointer-events-none absolute inset-x-0 z-[121] px-4 will-change-transform max-md:hidden md:px-12',
                className,
            )}
        >
            <p
                className={cn(
                    'm-0 text-right text-[7.76rem] leading-none font-medium tracking-[-0.05em] whitespace-nowrap',
                    'blur-[6px]',
                    // Darker than the unblurred mark was. #f6f6f6 is nine
                    // units off white — enough to read as a crisp watermark,
                    // but a 10px blur spreads those nine units across 20px and
                    // they vanish. The blur has to be paid for in contrast.
                    isDark ? 'text-[#3a3a3a]' : 'text-[#e6e6e6]',
                    markClassName,
                )}
            >
                {title}
            </p>
        </div>
    );
};

export const DirectoryBrowsePage = ({
    entityType,
    verticalTitle,
    theme = 'light',
    intro,
    tabs,
    categoryCarousel,
    pillBar,
    sidebar,
    rows,
    overlayOpen = false,
    belowGrid,
}: DirectoryBrowsePageProps) => {
    const hydrated = useHydrated();
    // A Projects / Spaces / Styles switch lands with the pill bar at the top.
    useScrollToPillBar();
    const isDark = theme === 'dark';
    const gateReached = useContentGateReached();
    // Whether this page is closing its grid with the gate at all, as opposed to
    // whether the reader has arrived at it.
    const gateActive = useContentGateActive();
    // Filter sidebar starts closed (grid full-width); the pill-bar toggle opens
    // it. The open state is persisted per entity so it survives navigating into
    // a detail page and back — and a full refresh — which keeps the grid columns
    // (and thus the scroll restore) consistent. Only meaningful when a sidebar
    // slot is supplied.
    const { open: filtersOpen, toggle: toggleFilters } =
        useDirectoryFilterOpenState(entityType);
    const hasSidebar = !!sidebar;
    // Grid-scoped "Start an enquiry list" island prompt (bulk mode off): shown
    // while the reading line is inside the grid, so the island stays free up top.
    const rowsRef = useRef<HTMLElement>(null);
    // Scroll behaviour lives in DirectoryUniformGrid, which owns this route's
    // scroll state: a category PUSH lands on the head of the grid, a filter or
    // sort PUSH holds the current position, and POP restores the saved depth.
    const gridInView = useSectionInView(rowsRef);

    return (
        <MagicIslandProvider>
            <BulkEnquiryEnabledProvider value={true}>
                <DirectoryBrowseThemeProvider value={theme}>
                    <DirectoryFilterDrawerProvider
                        value={{
                            open: filtersOpen,
                            toggle: toggleFilters,
                            hasSidebar,
                        }}
                    >
                        {/* Full-bleed dark background so the editorial theme runs edge to
                    edge beyond the centred max-width content. `focus-ring-on-dark`
                    flips the inherited focus-ring token for the whole subtree, so
                    keyboard rings read off-white against this ground. */}
                        <div
                            className={cn(
                                isDark && 'bg-[#141414] focus-ring-on-dark',
                            )}
                        >
                            {/* Sections cap their content individually (rather
                                than one shared max-width column) so the
                                carousel and pill-bar strips can run full-bleed
                                to the viewport edge on >1920 screens while
                                everything else centres in the 1920 container. */}
                            <div
                                data-testid="directory-browse-page"
                                data-entity-type={entityType}
                                // `isolate` scopes the wordmark's negative
                                // z-index to this column. Without it the mark
                                // resolves against a stacking context further
                                // up and an opaque white ancestor between the
                                // two paints straight over it.
                                className="relative flex w-full flex-col"
                            >
                                {/* The vertical's name, set very large and
                                    barely off the page colour, centred on the
                                    header's logomark. Named from `entityType`,
                                    not the active category — it is the library
                                    you are in, and it stays put however deep
                                    you go.

                                    It has to clear the site header, which is
                                    opaque and z-120: safe because the header's
                                    right half is empty across that band, and
                                    stays empty when the nav menu opens.
                                    pointer-events-none so it can never
                                    intercept a click; aria-hidden because the
                                    vertical is already announced by the nav and
                                    the breadcrumb. Desktop only — below md the
                                    page's own h1 carries the name.

                                    The offset is per-theme because the two
                                    headers occupy space differently: the light
                                    header is solid and in-flow, so the page
                                    column already starts below it and the mark
                                    reaches back up over it. The dark header is
                                    transparent and absolute, so the column
                                    starts at the viewport top and the mark has
                                    to come down to meet the logomark instead —
                                    a shared offset puts it off-screen. */}
                                <VerticalWordmark
                                    isDark={isDark}
                                    title={
                                        verticalTitle ??
                                        VERTICAL_TITLE[entityType]
                                    }
                                    className={
                                        isDark
                                            ? 'top-[14.55rem]'
                                            : '-top-[9.1rem]'
                                    }
                                />
                                {/* Leading spacer between the top nav and the page
                            content. On the dark theme the site header runs
                            transparent and absolute (it overlaps rather than
                            reserving layout height), so the spacer must also clear
                            the header — the Figma's 376px Top Nav + 160px gap —
                            instead of the 160px used under the solid header.
                            Mobile has no dark Figma: the dark value is the
                            measured 76px fixed header plus the light theme's
                            48px gap, keeping first-content rhythm identical
                            across themes instead of inheriting a desktop-scaled
                            void. */}
                                {/* The dark value is the light one plus the
                                    header the transparent theme has to clear,
                                    so both themes open on the same gap between
                                    the header and the first line of copy. Only
                                    the light half carries the design's spacing
                                    — the rest is the header's own height and
                                    must not be scaled with it. */}
                                <SectionSpacer
                                    height={isDark ? 506 : 128}
                                    mobileHeight={isDark ? 114 : 38}
                                    unit="rem"
                                />

                                {intro ? (
                                    <>
                                        <section
                                            data-section="intro"
                                            className={cn(
                                                CONTENT_CAP,
                                                'px-4 md:px-12',
                                            )}
                                        >
                                            {intro}
                                        </section>
                                        {/* The pill row's sticky band carries
                                            its own top padding, so this spacer
                                            only makes up the difference to the
                                            intended gap. Every spacer on this
                                            page sits at 80% of its Figma
                                            value — the design's rhythm, tighter. */}
                                        <SectionSpacer
                                            height={tabs ? 90 : 67}
                                            mobileHeight={tabs ? 40 : 45}
                                            unit="rem"
                                        />
                                    </>
                                ) : null}

                                {/* Browse-mode tabs (Figma 5762:114114), on
                                    the same content inset as the intro so the
                                    tab rule lines up with the statement. */}
                                {tabs ? (
                                    <>
                                        <section
                                            data-section="browse-tabs"
                                            className={cn(
                                                CONTENT_CAP,
                                                'px-4 md:px-12',
                                            )}
                                        >
                                            {tabs}
                                        </section>
                                        <SectionSpacer
                                            height={53}
                                            mobileHeight={24}
                                            unit="rem"
                                        />
                                    </>
                                ) : null}

                                {/* Category-card carousel (includes its own heading + separator) */}
                                <section data-section="category-carousel">
                                    {categoryCarousel}
                                </section>

                                {/* mobileHeight 0: the sticky pill bar carries
                                    its own mobile padding, so a spacer here
                                    reads as a random gap under the carousel. */}
                                {categoryCarousel ? (
                                    <SectionSpacer
                                        height={128}
                                        mobileHeight={0}
                                        unit="rem"
                                    />
                                ) : null}

                                {/* Filter / category-pill bar */}
                                {/* Below md the pill bar snaps to the fixed
                                    header's bottom edge (Pinterest-style) and
                                    the grid scrolls beneath it — its own
                                    background, sitting under the header's
                                    z-120. 4.75rem = the header's 76px at the
                                    414 anchor; rem tracks the vw-scaled root
                                    so the offset follows the header at every
                                    width. Desktop keeps the in-flow bar. */}
                                {/* The sticky band, and the reason it is a box at
                                    all: a sticky element only travels inside its
                                    containing block, so this wrapper is what
                                    decides where the pill bar lets go. It spans
                                    the bar and the grid and stops there — the bar
                                    belongs to the grid, and once the grid has
                                    scrolled past there is nothing left for it to
                                    filter. Without it the bar's block was the
                                    whole page column, so it stayed pinned over the
                                    SEO copy and down to the footer.

                                    Mirrors the parent's `flex w-full flex-col` so
                                    the `md:contents` section below still resolves
                                    its children as flex items of a column, exactly
                                    as when they were the parent's own. */}
                                <div className="flex w-full flex-col">
                                    <section
                                        data-section="pill-bar"
                                        // qhd:overflow-x-clip — the bar's pill
                                        // scroller breaks out to the viewport edge
                                        // with a 100vw-based margin; clip here so a
                                        // classic (non-overlay) scrollbar's width
                                        // can't create a horizontal page scroll.
                                        className={cn(
                                            'max-md:sticky max-md:top-[4.75rem] max-md:z-[110] max-md:pt-2 max-md:pb-2.5 qhd:overflow-x-clip',
                                            // Where a view switch lands: level
                                            // with the sticky offset, so the
                                            // band sits exactly where it will
                                            // pin. Desktop pins at 0 and the
                                            // header scrolls away, so only the
                                            // mobile inset is worth naming.
                                            'max-md:scroll-mt-[4.75rem]',
                                            // Below md the section is the sticky
                                            // element, not the row inside it, so
                                            // the gate dismissal has to be set
                                            // here too or the band stays behind
                                            // its own hidden contents.
                                            'max-md:transition-[opacity,transform] max-md:duration-200',
                                            gateReached &&
                                                'max-md:pointer-events-none max-md:-translate-y-2 max-md:opacity-0',
                                            // Desktop: this section stops being a
                                            // box, so the pill row inside sticks
                                            // against the page column rather than
                                            // against these two rows. A sticky
                                            // element only travels within its
                                            // parent's box — kept as a section, the
                                            // bar would unstick as soon as the
                                            // heading row below it scrolled past.
                                            // The pill row carries its own sticky
                                            // and frosted fill (DirectoryPillBar).
                                            'md:contents',
                                            isDark
                                                ? 'max-md:bg-[#141414]'
                                                : 'max-md:bg-white',
                                        )}
                                    >
                                        {pillBar}
                                    </section>

                                    {/* Main grid, with the optional facet sidebar collapsing
                            in on the left when Show Filters is toggled. */}
                                    {/* max-md:pt-6 — the visual gap below the pill
                                    bar lives here (outside the sticky strip) so
                                    it scrolls under the bar once stuck. */}
                                    <section
                                        data-section="rows"
                                        ref={rowsRef}
                                        className={cn(
                                            CONTENT_CAP,
                                            'max-md:pt-6',
                                        )}
                                    >
                                        {hasSidebar ? (
                                            <CollapsiblePageLayout
                                                sidePanel={sidebar}
                                                hideSidePanel={!filtersOpen}
                                                disableMinHeight
                                                paddingClassName="px-0"
                                            >
                                                {rows}
                                            </CollapsiblePageLayout>
                                        ) : (
                                            rows
                                        )}
                                    </section>
                                </div>

                                {/* Below-grid SEO content (recently viewed, explore
                            all categories, newspaper FAQ). Full width, outside
                            the sidebar layout — reachable now that the grid's
                            capped load lets users scroll past it. */}
                                {/* Withheld behind the gate along with the
                                    tail of the grid: past the wall these
                                    sections are more of the catalogue by another
                                    route, and leaving them reachable makes the
                                    gate look like something to scroll around.

                                    Safe for search: `gated` is false for a
                                    crawler (resolve-content-gate bails on
                                    `device.isBot` before anything else), so a
                                    bot still gets every section and the indexed
                                    page is unchanged. Only a logged-out human
                                    loses them. */}
                                {belowGrid && !gateActive ? (
                                    <section
                                        data-section="below-grid"
                                        className={CONTENT_CAP}
                                    >
                                        {belowGrid}
                                    </section>
                                ) : null}
                            </div>
                        </div>
                    </DirectoryFilterDrawerProvider>
                </DirectoryBrowseThemeProvider>
            </BulkEnquiryEnabledProvider>

            {/* Fixed chrome, so it sits outside the theme provider's subtree
                and takes the theme by prop. */}
            {hydrated ? <BackToTop white={!isDark} overDark={isDark} /> : null}
            {hydrated ? <MagicIsland /> : null}
            {hydrated ? (
                <BulkEnquire
                    gridInView={gridInView && !overlayOpen}
                    entityType={entityType}
                />
            ) : null}
        </MagicIslandProvider>
    );
};
